import { z } from 'zod';
import { phoneSchema, storedPhoneSchema } from '../phone/phone.js';
/**
 * Parties of a transfer (transfers.md section 4.2, owner decision 4).
 *
 * The person present at the branch fills a level-A form (full name, phone,
 * document type, number, country) and signs; the absent party is level B
 * (full name and phone) and never signs. Which role is present depends on the
 * transfer direction (TRF-40):
 *
 * - BANK_TO_CASH (من بنك إلى نقد): المستلم is present and collects the cash;
 *   المرسل made the bank transfer and is usually absent.
 * - CASH_TO_BANK (من نقد إلى بنك): المرسل is present and hands over the cash;
 *   المستلم receives the bank transfer and is usually absent.
 *
 * With المرسل هو المستلم one level-A form is filled, stored in the receiver
 * slot, and one customer file is used for both roles.
 *
 * No document photo is taken or stored (owner decision 5).
 *
 * Every party phone, typed on the tablet or by the employee, follows the
 * shared phone rule (customers.md section 9, owner decision of 23 October
 * 2026): a mobile only, normalised to E.164 by `phoneSchema`.
 */
export const DOCUMENT_TYPES = ['NATIONAL_ID', 'PASSPORT'];
export const documentTypeSchema = z.enum(DOCUMENT_TYPES);
/** ISO 3166-1 alpha-2 issuing country. */
export const countryCodeSchema = z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z]{2}$/, 'must be an ISO alpha-2 country code');
export const fullNameSchema = z.string().trim().min(1).max(200);
export const documentNumberSchema = z.string().trim().min(1).max(64);
/** A level-A party: the person present (the receiver of a BANK_TO_CASH transfer). */
export const receiverInputSchema = z.object({
    fullName: fullNameSchema,
    phone: phoneSchema,
    documentType: documentTypeSchema,
    documentNumber: documentNumberSchema,
    documentCountry: countryCodeSchema,
});
/** A level-B party: the absent one (the sender of a BANK_TO_CASH transfer). */
export const senderInputSchema = z.object({
    fullName: fullNameSchema,
    phone: phoneSchema,
});
/**
 * A party in either role, as sent by the employee screen. The document is
 * required for the present party and ignored for the absent one; the server
 * applies that rule with the transfer's direction (`presentPartyRole`).
 */
export const partyInputSchema = senderInputSchema.extend({
    documentType: documentTypeSchema.optional(),
    documentNumber: documentNumberSchema.optional(),
    documentCountry: countryCodeSchema.optional(),
});
/**
 * A party of an edited transfer: its phone is read with `storedPhoneSchema`,
 * so an unchanged stored phone of an excluded country saves; the edit
 * service refuses a changed one (customers.md section 9.7).
 */
export const editPartyInputSchema = partyInputSchema.extend({ phone: storedPhoneSchema });
/**
 * The Customers link of a party once posted: PENDING while the outbox has not
 * succeeded (بانتظار المزامنة), SYNCED once registered.
 */
export const partySyncStatusSchema = z.enum(['PENDING', 'SYNCED', 'FAILED']);
/**
 * A party as stored on the transfer. The document is present only for the
 * party who was present at the branch (level A); it is null for the absent one.
 */
export const partySnapshotSchema = z.object({
    fullName: z.string(),
    phone: z.string(),
    documentType: documentTypeSchema.nullable(),
    documentNumber: z.string().nullable(),
    documentCountry: z.string().nullable(),
    customerId: z.string().nullable(),
    /** Customers refused the document (409): shows "الوثيقة مسجلة لعميل آخر". */
    documentConflict: z.boolean(),
    /**
     * Customers holds this phone as verified with a WhatsApp code (its
     * `phoneVerifiedAt` was set when the party was registered): shows رقم مؤكد.
     */
    phoneVerified: z.boolean(),
});
export const receiverSnapshotSchema = partySnapshotSchema;
export const senderSnapshotSchema = partySnapshotSchema;
/**
 * The Customers platform's answer for a phone, shown on the employee screen
 * so the employee can compare. Never shown on the tablet.
 */
export const customerMatchSchema = z.object({
    status: z.enum(['FOUND', 'NOT_FOUND', 'UNAVAILABLE']),
    customer: z
        .object({
        id: z.string(),
        phone: z.string(),
        name: z.string().nullable(),
        level: z.enum(['A', 'B', 'C']),
        /** Customers verified this phone (`phoneVerifiedAt` set): no code is asked. */
        phoneVerified: z.boolean().optional(),
    })
        .nullable(),
});
//# sourceMappingURL=parties.js.map